import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';
import { IRegEdActionNeededItem } from './types';

export const REGED_ACTION_NEEDED_QUERY_KEY = 'regEdActionNeeded';

/**
 * Everything in the account whose compliance decision has landed but that still needs the advisor,
 * each tagged with a `reason`. Deliberately date-independent: a held or rejected piece drops out of
 * the forward-looking campaign views, so those surfaces alone can hide work that is still owed.
 */
export const useRegEdActionNeededQuery = ({
	enabled = true,
	impersonationContext,
}: {
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
} = {}) => {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryKey: [REGED_ACTION_NEEDED_QUERY_KEY, impersonationContext?.account?.id],
		queryFn: () =>
			userSession.webServiceHelper.callAsync<IRegEdActionNeededItem[]>({
				path: Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: 'compliance/regEd/actionNeeded',
				}),
				method: 'GET',
				throwOn404: false,
			}),
		meta: {
			toastErrorMessage: 'Failed to load the compliance items that need your attention.',
		},
	});
};

export const invalidateRegEdActionNeeded = () => {
	return reactQueryClient.invalidateQueries({ queryKey: [REGED_ACTION_NEEDED_QUERY_KEY] });
};
